// Drift: kortet med steamernes position (del af SupabaseApi – se supabase.ts). kort_data (20261029200000_kontor3.sql) giver
// dagens steamere, arbejdstider, fravær, opgaver og GPS-positioner (under 15 min. gamle, kun med sporing slået til).
// Status, ETA og beregnet placering regnes her som i testversionen.
import type { Api, SteamerPosition } from './api';
import type { Position, Steamer } from '../../core/typer';
import { iDag, lokalMin } from '../../core/dato';
import { koeretidMin, postnrPunkt, type Punkt } from '../../core/geo';
import { fraDb } from '../../core/raekker';
import { tilVisning } from './sbKontor';
import type { SbKontekst } from './sbKatalog';

type Raa = { steamere: Record<string, unknown>[]; arbejdstider: { steamer_id: string; start: number; slut: number }[]; fravaer: string[];
  bookinger: Parameters<typeof tilVisning>[0][]; positioner: { steamer_id: string; lat: number; lng: number; tid: string }[] };

/** Fast punkt for en adresse: postnummerets midte + en lille fast forskydning pr. nøgle (som i testversionen). */
function punktFor(postnr: string, noegle: number | string): Punkt | null {
  const p = postnrPunkt(postnr); if (!p) return null;
  const h = [...String(noegle)].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
  return { lat: p.lat + ((h % 100) - 50) / 45000, lng: p.lng + (((h >> 7) % 100) - 50) / 30000 };   // højst ~120 m – bliver på land
}

export class SbKort {
  constructor(private readonly c: SbKontekst) {}

  positioner: Api['positioner'] = async () => {
    const d = (await this.c.k(this.c.sb.rpc('kort_data', { k: await this.c.kraevKlient(), p_dato: iDag() }))) as Raa;
    const nu = lokalMin(new Date()), tid = new Date().toISOString();
    const opgaver = d.bookinger.map(tilVisning);
    return d.steamere.map((r) => fraDb.steamer(r) as Steamer).map((s): SteamerPosition => {
      const opg = opgaver.filter((b) => b.steamer_id === s.id);
      const gps = d.positioner.find((p) => p.steamer_id === s.id);
      const fravaer = d.fravaer.includes(s.id);
      // Placering: GPS – ellers beregnet ud fra dagens plan (hjemme, på vej mellem to opgaver, eller hos kunden)
      let position: Position | null = null;
      if (!fravaer) {
        if (gps) position = { lat: gps.lat, lng: gps.lng, tid: gps.tid, kilde: 'gps' };
        else {
          let fra = punktFor(s.start_postnr, s.id);
          for (const b of opg) {
            const p = punktFor(b.postnr, b.id);
            if (nu >= b.start! && nu < b.slut!) { fra = p; break; }
            if (nu < b.start!) {
              if (fra && p) { const kt = koeretidMin(fra, p); const f = nu <= b.start! - kt ? 0 : Math.min(1, (nu - (b.start! - kt)) / Math.max(1, kt)); fra = { lat: fra.lat + (p.lat - fra.lat) * f, lng: fra.lng + (p.lng - fra.lng) * f }; }
              else fra = p ?? fra;
              break;
            }
            fra = p ?? fra;
          }
          position = fra ? { ...fra, tid, kilde: 'beregnet' } : null;
        }
      }
      const aktuel = opg.find((b) => b.status === 'i_gang' || (nu >= b.start! && nu < b.slut! && b.status !== 'faerdig')) ?? null;
      const naeste = opg.find((b) => b.start! > nu && b.status === 'godkendt' && b !== aktuel) ?? null;
      const arb = d.arbejdstider.find((a) => a.steamer_id === s.id);
      let etaMin: number | null = null, forsinketMin: number | null = null;
      if (naeste && position) {
        const p = punktFor(naeste.postnr, naeste.id);
        if (p) { etaMin = koeretidMin(position, p); const ank = (aktuel ? Math.max(nu, aktuel.slut!) : nu) + etaMin; if (ank > naeste.start! + 5 && naeste.start! - nu < 120) forsinketMin = ank - naeste.start!; }
      }
      if (naeste?.forsinket) forsinketMin = Math.max(forsinketMin ?? 0, naeste.forsinket);
      if (aktuel?.forsinket && aktuel.status !== 'i_gang') forsinketMin = Math.max(forsinketMin ?? 0, aktuel.forsinket);
      const status: SteamerPosition['status'] = fravaer ? 'fri' : !arb || nu < arb.start - 30 || nu > arb.slut + 30 ? (opg.some((b) => b.status !== 'faerdig') ? 'paa_vej' : 'fri')
        : aktuel ? 'hos_kunde' : naeste ? 'paa_vej' : 'ledig';
      return { steamer: s, position, status, naeste, aktuel, forsinketMin, etaMin };
    });
  };
  saetSporing: Api['saetSporing'] = async (steamer_id, til) => {
    await this.c.k(this.c.sb.rpc('saet_sporing', { k: await this.c.kraevKlient(), p_steamer: steamer_id, p_til: til }));
    this.c.skift();
  };
}
